// src/pages/CompanyDirectory.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import utilityDirectoryList from '../data/utilitydirectorylist.json'; // Adjust path as needed
import RandomAd from '../components/RandomAd'; // Importing the RandomAd component

const AdContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 40px 0;
  
  @media (max-width: 768px) {
    margin: 30px 0;
  }
`;
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 50px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const PageTitle = styled.h1`
  font-size: 48px;
  font-weight: 600;
  background: linear-gradient(45deg, #1d1d1f, #424245);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 16px;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 21px;
  color: #86868b;
  font-weight: 400;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 18px;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const FilterSection = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  gap: 20px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const FilterSelect = styled.select`
  padding: 14px 24px;
  font-size: 17px;
  border: 1px solid #d2d2d7;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  min-width: 220px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #0071e3;
    box-shadow: 0 0 0 4px rgba(0, 113, 227, 0.1);
  }
  
  &:hover {
    border-color: #86868b;
  }

  @media (max-width: 480px) {
    min-width: 100%;
    padding: 16px 20px;
    font-size: 16px;
  }
`;

const ResultsCount = styled.div`
  text-align: center;
  margin-bottom: 30px;
  color: #86868b;
  font-size: 17px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 25px;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const CompanyCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 18px;
  padding: 28px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    background: rgba(255, 255, 255, 0.95);
  }

  @media (max-width: 768px) {
    padding: 24px;
    margin: 0 4px;
  }
`;

const CompanyName = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 16px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 14px;
  }
`;

const CompanyDetail = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f7;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
`;

const DetailLabel = styled.span`
  font-weight: 500;
  color: #86868b;
  font-size: 15px;
`;

const DetailValue = styled.span`
  font-weight: 400;
  color: #1d1d1f;
  font-size: 16px;
  text-align: right;
`;

const WebsiteLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  background: linear-gradient(135deg, #0071e3, #0077ed);
  color: white;
  text-decoration: none;
  font-weight: 500;
  border-radius: 12px;
  transition: all 0.3s ease;
  margin-top: 16px;
  font-size: 16px;
  width: 100%;
  text-align: center;

  &:hover {
    background: linear-gradient(135deg, #0077ed, #0082f3);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(0, 113, 227, 0.3);
  }

  @media (max-width: 768px) {
    padding: 14px 24px;
    font-size: 17px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #86868b;
  font-size: 19px;
  grid-column: 1 / -1;

  @media (max-width: 768px) {
    padding: 60px 20px;
    font-size: 17px;
  }
`;

const CompactViewToggle = styled.button`
  display: none;
  margin: 0 auto 20px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #d2d2d7;
  border-radius: 10px;
  font-size: 15px;
  color: #1d1d1f;
  cursor: pointer;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    display: block;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.9);
    border-color: #86868b;
  }
`;

const CompanyDirectory = () => {
  const [selectedCountry, setSelectedCountry] = useState('All');
  const [filteredData, setFilteredData] = useState([]);
  const [compactView, setCompactView] = useState(false);

  // Available countries from your specification
  const countries = [
    'All',
    'United States',
    'Canada', 
    'UK',
    'France',
    'Germany',
    'Australia'
  ];

  useEffect(() => {
    // Filter data based on selected country
    if (selectedCountry === 'All') {
      setFilteredData(utilityDirectoryList);
    } else {
      const filtered = utilityDirectoryList.filter(
        company => company.Country === selectedCountry
      );
      setFilteredData(filtered);
    }
  }, [selectedCountry]);

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
  };

  return (
    <PageContainer>
      <HeaderSection>
        <PageTitle>Company Directory</PageTitle>
        <PageSubtitle>
          Explore power utility companies across different regions and countries. 
          Filter by country to find specific opportunities.
        </PageSubtitle>
      </HeaderSection>
      {/* Strategic Ad Placement 1: After Header Section */}
      {/* This captures user attention immediately after understanding the page purpose */}
      <AdContainer>
        <RandomAd />
      </AdContainer>


      <FilterSection>
        <FilterSelect 
          value={selectedCountry} 
          onChange={handleCountryChange}
        >
          {countries.map(country => (
            <option key={country} value={country}>
              {country === 'All' ? 'All Countries' : country}
            </option>
          ))}
        </FilterSelect>
      </FilterSection>

      <CompactViewToggle onClick={() => setCompactView(!compactView)}>
        {compactView ? 'Show Detailed View' : 'Show Compact View'}
      </CompactViewToggle>

      <ResultsCount>
        Showing {filteredData.length} company{filteredData.length !== 1 ? 'ies' : ''}
        {selectedCountry !== 'All' && ` in ${selectedCountry}`}
      </ResultsCount>

      <CardsContainer>
        {filteredData.length > 0 ? (
          filteredData.map((company, index) => (
            <CompanyCard key={index}>
              <CompanyName>{company['Company Name']}</CompanyName>
              
              {!compactView && (
                <>
                  <CompanyDetail>
                    <DetailLabel>Country</DetailLabel>
                    <DetailValue>{company.Country}</DetailValue>
                  </CompanyDetail>
                  
                  <CompanyDetail>
                    <DetailLabel>Region</DetailLabel>
                    <DetailValue>{company.Region}</DetailValue>
                  </CompanyDetail>
                  
                  <CompanyDetail>
                    <DetailLabel>Company Type</DetailLabel>
                    <DetailValue>{company['Company Type']}</DetailValue>
                  </CompanyDetail>
                </>
              )}
              
              <WebsiteLink 
                href={company['Website URL']} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Visit Career Website
              </WebsiteLink>
            </CompanyCard>
          ))
        ) : (
          <EmptyState>
            No companies found for the selected country.
          </EmptyState>
        )}
      </CardsContainer>
      {/* Strategic Ad Placement 2: After Results Section */}
      {/* This captures users after they've engaged with the content and are considering next steps */}
      <AdContainer>
        <RandomAd />
      </AdContainer>
    </PageContainer>
  );
};

export default CompanyDirectory;